<template>
  <modal-wrapper
    ref="modalRef"
    :close-on-ok-click="false"
    :content-overflow-y="'visible'"
    :header="t('addCheckBotConfigModal.newCheckbotConfig')"
    @ok-button-click="handleModalOk"
  >
    <div>
      <label>{{ t('addCheckBotConfigModal.configTemplate') }}</label>
      <styled-multiselect
        v-model="selectedTemplate"
        :disabled="isStartEmpty"
        :is-error="isTemplateSelectError"
        :label="getSelectLabel"
        :options="templates"
      >
        <template #option="option">
          <div v-tooltip.top="option.option.option.description">
            {{ getSelectLabel(option.option.option) }}
          </div>
        </template>
      </styled-multiselect>
      <message-wrapper v-if="isTemplateSelectError" simple-error>{{
        t('addCheckBotConfigModal.templateOrStartEmpty')
      }}</message-wrapper>
    </div>
    <div class="checkbox-container">
      <PVCheckbox
        v-model="isStartEmpty"
        binary
        input-id="start-empty-checkbox"
        @value-change="handleStartEmptyChange"
      />
      <label for="start-empty-checkbox">{{
        t('addCheckBotConfigModal.startEmpty')
      }}</label>
    </div>
  </modal-wrapper>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import StyledMultiselect from '@/components/StyledMultiselect/StyledMultiselect.vue';
  import { CheckBotConfigsService } from '@/services';
  import { ITemplateSummaryDto } from 'types';
  import { onMounted } from 'vue';
  import { useModalWrapper } from '../ModalWrapper';
  import { Route, routeParams } from '@/routes';

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const router = useRouter();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const props = defineProps<{
    projectId: string;
    supplierId?: string;
  }>();

  const isFetchingTemplates = ref(false);
  const isStartEmpty = ref(false);
  const isSubmitted = ref(false);
  const selectedTemplate = ref<ITemplateSummaryDto | null>(null);
  const templates = ref<ITemplateSummaryDto[]>([]);

  const isTemplateSelectError = computed(() => {
    return (
      isSubmitted.value && !(isStartEmpty.value || !!selectedTemplate.value)
    );
  });

  async function getTemplates(): Promise<void> {
    try {
      isFetchingTemplates.value = true;
      const { templates: fetchedTemplates } =
        await CheckBotConfigsService.getConfigTemplates();
      templates.value = fetchedTemplates;
    } catch {
      createErrorToast(
        t('addCheckBotConfigModal.errors.errorGettingTemplates'),
      );
    } finally {
      isFetchingTemplates.value = false;
    }
  }

  function getSelectLabel(template: ITemplateSummaryDto): string {
    return template.name;
  }

  function handleStartEmptyChange(isStartEmptyValue: boolean): void {
    isStartEmpty.value = isStartEmptyValue;
    if (isStartEmptyValue) {
      selectedTemplate.value = null;
    }
  }

  function handleModalOk(): void {
    isSubmitted.value = true;
    if (isTemplateSelectError.value) return;
    router.push({
      name: Route.CHECK_BOT_CONFIGURATOR,
      params: {
        [routeParams[Route.CHECK_BOT_CONFIGURATOR].projectId]: props.projectId,
      },
      query: {
        [routeParams[Route.CHECK_BOT_CONFIGURATOR].query.supplierId]:
          props.supplierId,
        [routeParams[Route.CHECK_BOT_CONFIGURATOR].query.templateId]:
          selectedTemplate.value?.id.toString(),
      },
    });
  }

  onMounted(() => {
    getTemplates();
  });
</script>

<style scoped lang="scss">
  .checkbox-container {
    margin-top: 1rem;
    display: flex;
    column-gap: 0.5rem;
  }
</style>
